import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Layout components
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

const ServiceDetailSection = styled.section`
  padding: 150px 0 100px;
  background-color: white;
  min-height: 80vh;
`;

const ServiceContainer = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const ServiceHeader = styled.div`
  margin-bottom: 50px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 20px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  svg {
    margin-right: 8px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #2563eb;
    
    svg {
      transform: translateX(-3px);
    }
  }
`;

const ServiceTitle = styled.h1`
  font-size: 2.8rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 16px;
  background-color: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  font-size: 2.2rem;
  color: #3b82f6;
`;

const ServiceContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 50px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Description = styled.div`
  margin-bottom: 40px;
  
  p {
    font-size: 1.1rem;
    color: #64748b;
    line-height: 1.8;
    margin-bottom: 20px;
  }
`;

const FeaturesList = styled.div`
  margin-bottom: 40px;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 20px;
  }
  
  ul {
    list-style-type: none;
    padding: 0;
  }
  
  li {
    position: relative;
    padding-left: 30px;
    margin-bottom: 15px;
    font-size: 1.05rem;
    color: #64748b;
    
    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 18px;
      height: 18px;
      background-color: #dbeafe;
      border-radius: 50%;
    }
    
    &:after {
      content: '';
      position: absolute;
      left: 7px;
      top: 13px;
      width: 4px;
      height: 8px;
      border: solid #3b82f6;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;

const CTASection = styled.div`
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 20px;
  }
  
  p {
    font-size: 1.05rem;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 25px;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
  font-weight: 600;
  padding: 14px 32px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const Sidebar = styled.div`
  background-color: #f8fafc;
  padding: 30px;
  border-radius: 12px;
  height: fit-content;
  
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 20px;
  }
`;

const ServicesList = styled.ul`
  list-style-type: none;
  padding: 0;
  
  li {
    margin-bottom: 15px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    display: block;
    color: #64748b;
    font-size: 1rem;
    padding: 12px 20px;
    border-radius: 8px;
    transition: all 0.3s ease;
    text-decoration: none;
    
    &:hover, &.active {
      background-color: #f1f5f9;
      color: #3b82f6;
    }
    
    &.active {
      font-weight: 600;
    }
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const servicesData = {
  'web-development': {
    title: 'Web Site Development',
    description: 'Our web development services provide comprehensive solutions to create stunning, responsive, and functional websites tailored to your business needs. We focus on creating user-friendly experiences with clean code and modern design principles.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
        <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"/>
        <path d="M6.854 4.646a.5.5 0 0 1 0 .708L4.207 8l2.647 2.646a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 0 1 .708 0zm2.292 0a.5.5 0 0 0 0 .708L11.793 8l-2.647 2.646a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708 0z"/>
      </svg>
    ),
    longDescription: [
      'Our web development team combines creativity with technical expertise to build websites that not only look great but also perform exceptionally well. We understand that your website is often the first interaction customers have with your business, so we ensure it leaves a lasting impression.',
      'We develop websites that are fully responsive, ensuring they function perfectly across all devices and screen sizes. Our development process includes thorough testing and optimization for speed, search engines, and user experience.'
    ],
    features: [
      'Responsive design for all devices and screen sizes',
      'Custom website development using modern frameworks',
      'User-friendly content management systems',
      'E-commerce functionality with secure payment gateways',
      'Search engine optimization (SEO) implementation',
      'Fast loading pages and performance optimization',
      'Integration with third-party services and APIs',
      'Regular maintenance and support packages'
    ]
  },
  'mobile-development': {
    title: 'Mobile Application Development',
    description: 'Customized mobile app or web application that specifically serves your need.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
        <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h6zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H5z"/>
        <path d="M8 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
      </svg>
    ),
    longDescription: [
      'Our mobile application development services deliver native and cross-platform applications that provide seamless experiences across iOS and Android devices. We specialize in creating intuitive, feature-rich apps that help businesses connect with their customers and streamline operations.',
      'From concept to deployment, our experienced team guides you through every step of the app development process. We focus on creating solutions that align with your business objectives while delivering exceptional user experiences.'
    ],
    features: [
      'Native iOS and Android app development',
      'Cross-platform app development (React Native, Flutter)',
      'Custom UI/UX design for mobile interfaces',
      'Offline functionality and data synchronization',
      'Integration with device features (camera, GPS, notifications)',
      'Secure authentication and data protection',
      'API development and third-party service integration',
      'App store submission and ongoing maintenance'
    ]
  },
  'content-writing': {
    title: 'Content Writing',
    description: 'Content that connects with your customers.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
        <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zM5 4h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1zm0 2h3a.5.5 0 0 1 0 1H5a.5.5 0 0 1 0-1z"/>
        <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1H1z"/>
      </svg>
    ),
    longDescription: [
      'Our professional content writing services help businesses communicate effectively with their target audience. We create compelling, engaging, and SEO-optimized content that resonates with readers and drives conversions.',
      'Whether you need website copy, blog posts, or marketing materials, our experienced writers craft content that aligns with your brand voice and business objectives. We conduct thorough research to ensure accuracy and relevance in every piece of content we create.'
    ],
    features: [
      'Website copywriting that converts visitors to customers',
      'SEO-optimized blog posts and articles',
      'Email marketing campaigns and newsletters',
      'Product descriptions and catalog content',
      'White papers and case studies',
      'Social media content creation',
      'Press releases and public relations materials',
      'Technical writing and documentation'
    ]
  },
  'ui-ux': {
    title: 'UI/UX',
    description: 'Our well researched UI and experience design builds a pleasing experience for your customers.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
        <path d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1h-7zM0 13a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 16 13V6a1.5 1.5 0 0 0-1.5-1.5h-13A1.5 1.5 0 0 0 0 6v7zm1.5.5A.5.5 0 0 1 1 13V6a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5h-13z"/>
      </svg>
    ),
    longDescription: [
      'Our UI/UX design services focus on creating intuitive, engaging, and user-friendly interfaces that enhance the overall user experience. We combine aesthetics with functionality to design interfaces that are not only visually appealing but also easy to navigate and use.',
      'Through careful research and user testing, we create designs that reflect your brand identity while meeting the needs and expectations of your target audience. Our goal is to create interfaces that delight users and drive engagement with your product or service.'
    ],
    features: [
      'User research and persona development',
      'Wireframing and prototyping',
      'Interactive and responsive design',
      'User flow optimization',
      'Usability testing and feedback implementation',
      'Design system creation',
      'Visual design and branding integration',
      'Accessibility compliance'
    ]
  },
  'seo': {
    title: 'Search Engine Optimization',
    description: 'Enhancing your online visibility and build authority in your business domain organically.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
        <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319z"/>
      </svg>
    ),
    longDescription: [
      "Our SEO services are designed to boost your website's visibility in search engine results, drive organic traffic, and increase conversions. We implement proven strategies that align with the latest search engine algorithms to improve your website's ranking for relevant keywords.",
      "Our comprehensive approach includes technical SEO, on-page optimization, content strategy, and off-page techniques. We provide regular reporting and analytics to track progress and continuously refine our strategies to achieve the best results for your business."
    ],
    features: [
      'Keyword research and competitive analysis',
      'On-page optimization and content enhancement',
      'Technical SEO audits and implementation',
      'Link building and off-page SEO strategies',
      'Local SEO for businesses with physical locations',
      'E-commerce SEO for online stores',
      'SEO-focused content creation and optimization',
      'Regular performance tracking and reporting'
    ]
  }
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];
  const [scrolled, setScrolled] = useState(true); // Always set scrolled to true for service detail pages
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  if (!service) {
    return (
      <>
        <Navbar scrolled={scrolled} />
        <ServiceDetailSection>
          <ServiceContainer>
            <h1>Service not found</h1>
            <BackButton to="/#services">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
              </svg>
              Back to Services
            </BackButton>
          </ServiceContainer>
        </ServiceDetailSection>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar scrolled={scrolled} />
      <ServiceDetailSection>
        <ServiceContainer>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <ServiceHeader>
              <BackButton to="/#services">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                </svg>
                Back to Services
              </BackButton>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
            </ServiceHeader>

            <ServiceContent>
              <MainContent>
                <Description>
                  {service.longDescription.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </Description>
                
                <FeaturesList>
                  <h3>Key Features</h3>
                  <ul>
                    {service.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </FeaturesList>
                
                <CTASection>
                  <h3>Ready to Get Started?</h3>
                  <p>Contact us today to discuss your project requirements and how we can help you achieve your business goals.</p>
                  <CTAButton 
                    as="a" 
                    href="/contact"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = '/contact';
                    }}
                  >
                    Contact Us
                  </CTAButton>
                </CTASection>
              </MainContent>
              
              <Sidebar>
                <h3>Our Services</h3>
                <ServicesList>
                  <li>
                    <Link to="/services/web-development" className={serviceId === 'web-development' ? 'active' : ''}>
                      Web Site Development
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/mobile-development" className={serviceId === 'mobile-development' ? 'active' : ''}>
                      Mobile Application Development
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/content-writing" className={serviceId === 'content-writing' ? 'active' : ''}>
                      Content Writing
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/ui-ux" className={serviceId === 'ui-ux' ? 'active' : ''}>
                      UI/UX
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/seo" className={serviceId === 'seo' ? 'active' : ''}>
                      Search Engine Optimization
                    </Link>
                  </li>
                </ServicesList>
              </Sidebar>
            </ServiceContent>
          </motion.div>
        </ServiceContainer>
      </ServiceDetailSection>
      <Footer />
    </>
  );
};

export default ServiceDetail;
